// Document type configuration for the compliance application
// This defines the supported document types with their groupings and validation rules

export const DOCUMENT_TYPES = [
  {
    id: 'restricted_markets',
    label: 'Restricted Markets',
    description: 'Market restriction policies and documentation (1 file max)',
    icon: '🌍',
    maxFiles: 1,
    allowedExtensions: ['.pdf', '.doc', '.docx', '.txt', '.md'],
    group: 'compliance'
  },
  {
    id: 'restricted_keyword',
    label: 'Restricted Keywords',
    description: 'Keyword restriction lists (multiple files allowed)',
    icon: '🔤',
    maxFiles: -1, // -1 means unlimited
    allowedExtensions: ['.csv', '.xlsx', '.xls', '.txt', '.json'],
    group: 'compliance'
  },
  {
    id: 'compliance_model',
    label: 'Compliance Model',
    description: 'Compliance model configuration (1 file max)',
    icon: '⚖️',
    maxFiles: 1,
    allowedExtensions: ['.json', '.yaml', '.yml', '.xml', '.txt'],
    group: 'compliance'
  },
  {
    id: 'policy_documents',
    label: 'Policy Documents',
    description: 'General policy and procedure documents',
    icon: '📋',
    maxFiles: -1,
    allowedExtensions: ['.pdf', '.doc', '.docx', '.txt', '.md'],
    group: 'policies'
  },
  {
    id: 'training_materials',
    label: 'Training Materials',
    description: 'Compliance training and educational content',
    icon: '🎓',
    maxFiles: -1,
    allowedExtensions: ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.txt', '.md'],
    group: 'training'
  },
  {
    id: 'audit_reports',
    label: 'Audit Reports',
    description: 'Compliance audit findings and reports',
    icon: '🔍',
    maxFiles: -1,
    allowedExtensions: ['.pdf', '.doc', '.docx', '.xlsx', '.xls', '.txt'],
    group: 'auditing'
  },
  {
    id: null,
    label: 'General Document',
    description: 'General purpose document upload',
    icon: '📄',
    maxFiles: -1,
    allowedExtensions: ['.pdf', '.doc', '.docx', '.txt', '.md', '.csv', '.json', '.xlsx', '.xls'],
    group: 'general'
  }
];

// Document groups for organization
export const DOCUMENT_GROUPS = {
  compliance: {
    id: 'compliance',
    label: 'Compliance Documents',
    description: 'Core compliance configuration and restriction documents',
    color: '#dc2626', // Red
    icon: '⚖️'
  },
  policies: {
    id: 'policies',
    label: 'Policy Documents',
    description: 'Organizational policies and procedures',
    color: '#2563eb', // Blue
    icon: '📋'
  },
  training: {
    id: 'training',
    label: 'Training Materials',
    description: 'Educational and training content',
    color: '#16a34a', // Green
    icon: '🎓'
  },
  auditing: {
    id: 'auditing',
    label: 'Audit & Reports',
    description: 'Audit findings and compliance reports',
    color: '#ea580c', // Orange
    icon: '🔍'
  },
  general: {
    id: 'general',
    label: 'General Documents',
    description: 'Miscellaneous documents and files',
    color: '#6b7280', // Gray
    icon: '📄'
  }
};

/**
 * Get document type by ID
 * @param {string|null} id - Document type ID
 * @returns {object|undefined} Document type object
 */
export const getDocumentTypeById = (id) => {
  return DOCUMENT_TYPES.find(type => type.id === id);
};

/**
 * Get document group by ID
 * @param {string} groupId - Group ID
 * @returns {object|undefined} Document group object
 */
export const getDocumentGroupById = (groupId) => {
  return DOCUMENT_GROUPS[groupId];
};

/**
 * Get document types by group
 * @param {string} groupId - Group ID
 * @returns {array} Array of document types in the group
 */
export const getDocumentTypesByGroup = (groupId) => {
  return DOCUMENT_TYPES.filter(type => type.group === groupId);
};

/**
 * Get all document groups with their associated types
 * @returns {array} Array of groups with their document types
 */
export const getGroupedDocumentTypes = () => {
  return Object.values(DOCUMENT_GROUPS).map(group => ({
    ...group,
    types: getDocumentTypesByGroup(group.id)
  }));
};

/**
 * Get file extension from filename
 * @param {string} filename - File name
 * @returns {string} File extension (including dot)
 */
export const getFileExtension = (filename) => {
  return filename.toLowerCase().substring(filename.lastIndexOf('.'));
};

/**
 * Check if file type is allowed for document type
 * @param {string} filename - File name
 * @param {object} documentType - Document type object
 * @returns {boolean} True if file type is allowed
 */
export const isFileTypeAllowed = (filename, documentType) => {
  const extension = getFileExtension(filename);
  return documentType.allowedExtensions.includes(extension);
};

/**
 * Validate file against document type constraints
 * @param {File} file - File object
 * @param {object} documentType - Document type object
 * @returns {string|null} Error message or null if valid
 */
export const validateFile = (file, documentType) => {
  // File size check (10MB max)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return 'File size must be less than 10MB';
  }

  // File type check
  if (!isFileTypeAllowed(file.name, documentType)) {
    return `File type not allowed for ${documentType.label}. Supported: ${documentType.allowedExtensions.join(', ')}`;
  }

  // File name validation
  if (file.name.length > 255) {
    return 'Filename too long (max 255 characters)';
  }

  // Check for invalid characters in filename
  const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;
  if (invalidChars.test(file.name)) {
    return 'Filename contains invalid characters';
  }

  return null; // Valid
};

/**
 * Get file type icon based on extension
 * @param {string} fileType - File extension or type
 * @returns {string} Emoji icon for the file type
 */
export const getFileTypeIcon = (fileType) => {
  const icons = {
    pdf: '📄',
    doc: '📝',
    docx: '📝',
    txt: '📃',
    md: '📃',
    csv: '📊',
    xlsx: '📊',
    xls: '📊',
    json: '🔧',
    yaml: '🔧',
    yml: '🔧',
    xml: '🔧',
    ppt: '📈',
    pptx: '📈'
  };
  return icons[fileType?.toLowerCase()] || '📄';
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if document type allows multiple files
 * @param {object} documentType - Document type object
 * @returns {boolean} True if multiple files are allowed
 */
export const allowsMultipleFiles = (documentType) => {
  return documentType.maxFiles === -1 || documentType.maxFiles > 1;
};

/**
 * Check if document type will replace existing documents
 * @param {object} documentType - Document type object
 * @returns {boolean} True if uploads will replace existing documents
 */
export const willReplaceExisting = (documentType) => {
  return documentType.maxFiles === 1;
};